import { DestroyRef, Inject, Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { AUTH_CLIENT, SESSION_CONFIG } from './session.tokens';
import { SessionConfig, SessionEndReason, SessionState } from './session.types';
import { ActivityMonitorService } from './activity-monitor.service';
import { CrossTabChannelService } from './cross-tab-channel.service';
import { AuthClient } from '../auth/auth-client';

function clamp(value: number, min = 0): number {
  return value < min ? min : value;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private cfg = inject(SESSION_CONFIG);
  private auth = inject(AUTH_CLIENT);
  private activity = inject(ActivityMonitorService);
  private channel = inject(CrossTabChannelService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private isBrowser = typeof window !== 'undefined';
  private storage = this.resolveStorage();
  private isAuthenticated = false;

  private lastActivity$ = new BehaviorSubject<number>(Date.now());
  private state$ = new BehaviorSubject<SessionState>({
    status: 'active',
    lastActivity: Date.now(),
    remainingMs: this.cfg.idleTimeoutMs,
    idleTimeoutMs: this.cfg.idleTimeoutMs,
    warnAtMs: this.cfg.warningBeforeMs
  });

  readonly sessionState$: Observable<SessionState> = this.state$.asObservable();
  readonly status$ = this.sessionState$.pipe(map((state) => state.status), distinctUntilChanged());
  readonly isActive$ = this.status$.pipe(map((status) => status === 'active' || status === 'warning'));

  private ending = false;

  init(): void {
    if (!this.isBrowser) {
      return;
    }

    this.ending = false;

    const authSub = this.auth.isAuthenticated$.subscribe((value) => {
      this.isAuthenticated = value;
    });

    const persisted = this.getPersistedLastActivity();
    this.lastActivity$.next(persisted);
    this.state$.next({ ...this.state$.value, lastActivity: persisted });

    // Disabled to prevent redirect loops during B2C auth flow
    // this.auth.events$?.pipe(filter((event) => event.type === 'logout')).subscribe(() => this.endSession('logout'));

    this.channel.messages$.subscribe((message) => {
      if (message.type === 'activity' && this.cfg.crossTab === 'global') {
        this.onActivityTimestamp(message.ts, false);
      }
      if (message.type === 'end') {
        this.endSession(message.reason as SessionEndReason);
      }
      // Disabled to prevent cross-tab logout loops
      // if (message.type === 'unauthorized' && this.cfg.endSessionOn401) {
      //   this.endSession('unauthorized');
      // }
    });

    this.activity.activity$.subscribe((timestamp) => this.onActivityTimestamp(timestamp, true));

    const timerSub = this.zone.runOutsideAngular(() =>
      interval(this.cfg.checkIntervalMs)
        .pipe(
          startWith(0),
          map(() => Date.now()),
          switchMap((now) => this.lastActivity$.pipe(map((last) => ({ now, last })))),
          map(({ now, last }) => this.calculateState(now, last)),
          distinctUntilChanged((a, b) => a.status === b.status && a.remainingMs === b.remainingMs && a.lastActivity === b.lastActivity),
          tap((state) => {
            if (state.status === 'expired') {
              this.zone.run(() => this.endSession('idle'));
              return;
            }
            this.state$.next(state);
          })
        )
        .subscribe()
    );

    this.destroyRef.onDestroy(() => {
      timerSub.unsubscribe();
      authSub.unsubscribe();
    });
  }

  markActivity(): void {
    this.activity.markActivity();
  }

  onUnauthorized(): void {
    if (!this.cfg.endSessionOn401 || !this.isAuthenticated || this.ending) {
      return;
    }
    const now = Date.now();
    if (this.isBrowser) {
      this.channel.post({ type: 'unauthorized', ts: now });
    }
    this.endSession('unauthorized');
  }

  async endSession(reason: SessionEndReason = 'manual'): Promise<void> {
    if (this.ending) {
      return;
    }
    this.ending = true;

    const current = this.state$.value;
    if (current.status !== 'ending' && current.status !== 'expired') {
      this.state$.next({ ...current, status: 'ending', reason });
    }

    if (this.isBrowser) {
      this.channel.post({ type: 'end', ts: Date.now(), reason });
    }

    try {
      await this.auth.logout({ redirectUri: this.cfg.postLogoutRedirectUri });
    } catch {
    }

    if (this.storage) {
      this.storage.removeItem(this.cfg.storageKeyLastActivity);
    }

    this.state$.next({ ...this.state$.value, status: 'expired', reason });

    if (!this.cfg.performRedirectOnLogout) {
      try {
        await this.router.navigateByUrl('/login');
      } catch {
      }
    }
  }

  private calculateState(now: number, last: number): SessionState {
    const elapsed = now - last;
    const remaining = clamp(this.cfg.idleTimeoutMs - elapsed, 0);
    const status: SessionState['status'] = remaining === 0 ? 'expired' : remaining <= this.cfg.warningBeforeMs ? 'warning' : 'active';
    return {
      status,
      lastActivity: last,
      remainingMs: remaining,
      idleTimeoutMs: this.cfg.idleTimeoutMs,
      warnAtMs: this.cfg.warningBeforeMs,
      reason: undefined
    };
  }

  private onActivityTimestamp(timestamp: number, broadcast: boolean): void {
    if (!timestamp) {
      return;
    }
    this.lastActivity$.next(timestamp);
    this.state$.next({ ...this.state$.value, lastActivity: timestamp });
    if (this.storage) {
      try {
        this.storage.setItem(this.cfg.storageKeyLastActivity, String(timestamp));
      } catch {
      }
    }
    if (broadcast && this.isBrowser && this.cfg.crossTab === 'global') {
      this.channel.post({ type: 'activity', ts: timestamp });
    }
  }

  private getPersistedLastActivity(): number {
    if (!this.storage) {
      return Date.now();
    }
    const value = Number(this.storage.getItem(this.cfg.storageKeyLastActivity));
    return Number.isFinite(value) && value > 0 ? value : Date.now();
  }

  private resolveStorage(): Storage | undefined {
    if (!this.isBrowser) {
      return undefined;
    }
    try {
      return this.cfg.persistStrategy === 'sessionStorage' ? window.sessionStorage : window.localStorage;
    } catch {
      return undefined;
    }
  }
}
